import { useState, useRef, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  PincoScreen,
  GreenCard,
  YellowCard,
  PincoSpinnerRing,
  PINCO_GREEN,
} from '../components/PincoLayout';

// ─── Constants ────────────────────────────────────────────────────────────────
const SYMBOL_H   = 72;
const STRIP_LEN  = 28;
const WIN_CHANCE = 0.25;
const SYMBOLS = ['🍒', '🌵', '🃏', '💎', '7️⃣', '🍋', '⭐', '🔔', '🎰', '🌸'];
const DURATIONS = [1700, 2150, 2600];

const WarnEmoji = styled.span`
  font-size: 26px;
  line-height: 1;
`;

const WarnRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const WarnTitle = styled.div`
  margin: 0 0 0 16px;
  font-weight: 400;
  letter-spacing: 0.00938em;
  display: inline-block;
  flex-shrink: 1;
  max-width: calc(100% - 56px);
  text-align: start;
  font-size: 14px;
  font-family: 'PT Mono', monospace;
  line-height: 1.2;
  color: #ffffff;
`;

// ─── Slot machine ─────────────────────────────────────────────────────────────
const buildReelKeyframes = (totalPx: number) => keyframes`
  0%   { transform: translateY(0px);                   filter: blur(0px); }
  2%   {                                                filter: blur(6px); }
  12%  { transform: translateY(-${totalPx * 0.32}px);  filter: blur(8px); }
  30%  { transform: translateY(-${totalPx * 0.58}px);  filter: blur(7px); }
  55%  { transform: translateY(-${totalPx * 0.78}px);  filter: blur(4px); }
  75%  { transform: translateY(-${totalPx * 0.91}px);  filter: blur(2px); }
  90%  { transform: translateY(-${totalPx * 0.98}px);  filter: blur(0.5px); }
  100% { transform: translateY(-${totalPx}px);          filter: blur(0px); }
`;

const ReelsWrapper = styled.div`
  position: relative; /* anchor for confetti */
`;

const ReelsRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  margin: 12px 0 6px;
`;

const ReelOuter = styled.div`
  flex: 1;
  aspect-ratio: 1 / 1;
  border: 2px solid #d4a96a;
  border-radius: 24px;
  background: #1d1d1d;
  overflow: hidden;
  position: relative;
`;

const FadeMask = styled.div`
  position: absolute;
  left: 0; right: 0;
  height: 18px;
  z-index: 2;
  pointer-events: none;
`;
const TopFade    = styled(FadeMask)`top: 0; background: linear-gradient(to bottom, #1d1d1d, transparent);`;
const BottomFade = styled(FadeMask)`bottom: 0; background: linear-gradient(to top, #1d1d1d, transparent);`;

const AnimatedStrip = styled.div<{ $totalPx: number; $duration: number }>`
  position: absolute;
  top: 0; left: 0; right: 0;
  animation: ${({ $totalPx }) => buildReelKeyframes($totalPx)} ${({ $duration }) => $duration}ms linear forwards;
`;

const SymbolCell = styled.div`
  height: ${SYMBOL_H}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  line-height: 1;
  user-select: none;
`;

// ─── Confetti ─────────────────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#6aaa3a', '#f5c542', '#e05252', '#5b9fd6', '#b45fd6', '#f08030'];
const CONFETTI_COUNT  = 32;

interface Particle { id: number; x: number; vx: number; vy: number; color: string; size: number; shape: 'circle' | 'rect'; }

const flyOut = (vx: number, vy: number) => keyframes`
  0%   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate(${vx}px, ${vy}px) rotate(${360 + Math.round(vx)}deg); opacity: 0; }
`;

const ConfettiPiece = styled.div<{ $p: Particle }>`
  position: absolute;
  top: 50%;
  left: ${({ $p }) => $p.x}%;
  width:  ${({ $p }) => $p.size}px;
  height: ${({ $p }) => $p.size}px;
  border-radius: ${({ $p }) => ($p.shape === 'circle' ? '50%' : '2px')};
  background: ${({ $p }) => $p.color};
  pointer-events: none;
  animation: ${({ $p }) => flyOut($p.vx, $p.vy)} ${() => 900 + Math.random() * 600}ms ease-out forwards;
`;

const ConfettiLayer = ({ active }: { active: boolean }) => {
  const particles = useMemo<Particle[]>(() => {
    if (!active) return [];
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const angle  = (Math.random() * 360) * (Math.PI / 180);
      const speed  = 60 + Math.random() * 140;
      return {
        id:    i,
        x:     20 + Math.random() * 60,
        vx:    Math.cos(angle) * speed,
        vy:    Math.sin(angle) * speed - 40, // bias upward
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size:  6 + Math.round(Math.random() * 6),
        shape: Math.random() > 0.5 ? 'circle' : 'rect',
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active || !particles.length) return null;
  return (
    <>
      {particles.map(p => (
        <ConfettiPiece key={p.id} $p={p} />
      ))}
    </>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const randomSym  = () => SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
const makeStrip  = (final: string) => [...Array.from({ length: STRIP_LEN }, randomSym), final];
const pickFinals = (): string[] => {
  if (Math.random() < WIN_CHANCE) {
    const s = randomSym();
    return [s, s, s];
  }
  // ensure at least 2 differ so it's not accidental win
  let res: string[];
  do { res = [randomSym(), randomSym(), randomSym()]; }
  while (res[0] === res[1] && res[1] === res[2]);
  return res;
};

// ─── Reel ─────────────────────────────────────────────────────────────────────
interface ReelProps { strip: string[]; settled: boolean; finalSymbol: string; duration: number; }

const Reel = ({ strip, settled, finalSymbol, duration }: ReelProps) => {
  const totalPx = STRIP_LEN * SYMBOL_H;
  return (
    <ReelOuter>
      <TopFade />
      <BottomFade />
      {settled ? (
        // Show ONLY the final symbol — no snap-back bug, inset: 0 centers it
        <SymbolCell style={{ position: 'absolute', inset: 0, height: 'auto' }}>
          {finalSymbol}
        </SymbolCell>
      ) : (
        <AnimatedStrip $totalPx={totalPx} $duration={duration}>
          {strip.map((sym, i) => <SymbolCell key={i}>{sym}</SymbolCell>)}
        </AnimatedStrip>
      )}
    </ReelOuter>
  );
};

// ─── Spin button ──────────────────────────────────────────────────────────────
const SpinBtn = styled.button`
  display: block;
  margin: 0 auto 10px;
  padding: 5px 10px;
  background: ${PINCO_GREEN};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  &:hover  { opacity: 0.88; }
  &:active { transform: scale(0.95); }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
`;

const WinBanner = styled.div`
  text-align: center;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  font-weight: 700;
  color: ${PINCO_GREEN};
  margin-bottom: 6px;
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface SlotProps { onBack: () => void; uuid?: string; timeLeft?: string; }

const PincoSlotPage = ({ onBack, uuid, timeLeft }: SlotProps) => {
  const { t } = useTranslation();

  const [finals,   setFinals]   = useState<string[]>(['🍒', '🌵', '🃏']);
  const [strips,   setStrips]   = useState<string[][]>([
    makeStrip('🍒'), makeStrip('🌵'), makeStrip('🃏'),
  ]);
  const [settled,  setSettled]  = useState([true, true, true]); // start settled
  const [spinning, setSpinning] = useState(false);
  const [win,      setWin]      = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const timersRef = useRef<NodeJS.Timeout[]>([]);

  const clear = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  useEffect(() => () => clear(), []);

  const handleSpin = () => {
    if (spinning) return;
    clear();
    setWin(false);
    setShowConfetti(false);

    const newFinals = pickFinals();
    const newStrips = newFinals.map(makeStrip);
    setFinals(newFinals);
    setStrips(newStrips);
    setSettled([false, false, false]);
    setSpinning(true);

    // Settle each reel individually
    DURATIONS.forEach((dur, idx) => {
      const t1 = setTimeout(() => {
        setSettled(prev => { const n = [...prev]; n[idx] = true; return n; });
        if (idx === DURATIONS.length - 1) {
          setSpinning(false);
          const isWin = newFinals[0] === newFinals[1] && newFinals[1] === newFinals[2];
          if (isWin) {
            setWin(true);
            setShowConfetti(true);
            const t2 = setTimeout(() => setShowConfetti(false), 1500);
            timersRef.current.push(t2);
          }
        }
      }, dur);
      timersRef.current.push(t1);
    });
  };

  return (
    <PincoScreen uuid={uuid} timeLeft={timeLeft} actions={[{ label: t('common.back'), onClick: onBack, variant: 'grey' }]}>
      <GreenCard>
        <WarnRow>
          <PincoSpinnerRing size={48}>
            <WarnEmoji>⚠️</WarnEmoji>
          </PincoSpinnerRing>
          <WarnTitle>
            {t('waiting.searchingRequisites')}
          </WarnTitle>
        </WarnRow>

      </GreenCard>

      <YellowCard>
        <div style={{ fontFamily: "'PT Mono', monospace", fontSize: 13, color: '#555', textAlign: 'center', lineHeight: 1.5 }}>
          {t('waiting.slotTeaser')}
        </div>
      </YellowCard>

      <ReelsWrapper>
        <ConfettiLayer active={showConfetti} />
        <ReelsRow>
          {finals.map((final, idx) => (
            <Reel
              key={idx}
              strip={strips[idx]}
              settled={settled[idx]}
              finalSymbol={final}
              duration={DURATIONS[idx]}
            />
          ))}
        </ReelsRow>
      </ReelsWrapper>

      {win && <WinBanner>🎉 Удача на вашей стороне!</WinBanner>}

      <SpinBtn onClick={handleSpin} disabled={spinning}>
        {spinning ? '...' : t('waiting.spin')}
      </SpinBtn>
    </PincoScreen>
  );
};

export default PincoSlotPage;
